import { SeoHead } from "@/components/common/SeoHead";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Heart, Sparkles, Sunset, UtensilsCrossed, Check, ArrowRight, Star } from "lucide-react";
import { useEffect } from "react";

const honeymoonFeatures = [
  { icon: Heart, title: "Romantic Touches", desc: "Special surprises and intimate moments" },
  { icon: Sparkles, title: "Luxury Stays", desc: "Hand-picked romantic accommodations" },
  { icon: Sunset, title: "Scenic Experiences", desc: "Breathtaking views and private excursions" },
  { icon: UtensilsCrossed, title: "Dining Experiences", desc: "Candlelit dinners and gourmet cuisine" },
];

const honeymoonDestinations = [
  {
    name: "Maldives",
    tagline: "Overwater Paradise",
    desc: "Crystal clear waters, private villas, and endless romance",
    image: "https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=600&q=80",
    rating: 4.9
  },
  {
    name: "Santorini",
    tagline: "Greek Island Dream",
    desc: "Sunset views, white-washed villages, and Mediterranean charm",
    image: "https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=600&q=80",
    rating: 4.8
  },
  {
    name: "Bali",
    tagline: "Island of Gods",
    desc: "Spiritual retreats, rice terraces, and tropical luxury",
    image: "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=600&q=80",
    rating: 4.9
  },
  {
    name: "Paris",
    tagline: "City of Love",
    desc: "Iconic romance, world-class dining, and timeless elegance",
    image: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=600&q=80",
    rating: 4.8
  },
];

const includedServices = [
  "Airport transfers with flower welcome",
  "Romantic room decoration on arrival",
  "Couples spa treatment",
  "Private candlelit dinner",
  "Sunset cruise or excursion",
  "Honeymoon cake and champagne",
  "Professional photo session",
  "24/7 concierge support"
];

export default function HoneymoonPackagesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div id="main-content" className="min-h-screen bg-white" data-testid="honeymoon-packages-page">
      <SeoHead
        title="Honeymoon Travel Packages"
        description="Plan your perfect honeymoon with Tripsyfy. Custom romantic travel packages to top honeymoon destinations with expert planning support."
        canonical="https://tripsyfy.com/services/honeymoon-packages"
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 lg:pt-28 pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-accent text-sm font-semibold tracking-wide uppercase mb-4">Our Services</p>
              <h1 className="heading-xl text-primary mb-6">Honeymoon Packages</h1>
              <p className="text-base lg:text-lg text-muted-foreground leading-relaxed">
                Begin your forever with a journey as beautiful as your love story. Our honeymoon packages are crafted to create magical moments you'll cherish for a lifetime.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {honeymoonFeatures.map((feature, i) => (
                <div key={feature.title} className="bg-white p-6 rounded-lg border border-border text-center">
                  <div className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="h-6 w-6 text-accent" />
                  </div>
                  <h3 className="font-bold text-primary mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.desc}</p>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Destinations */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="heading-lg text-primary mb-4">Dream Honeymoon Destinations</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                From tropical paradises to romantic cities, discover the perfect backdrop for your love story.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {honeymoonDestinations.map((dest, i) => (
              <AnimatedSection key={dest.name} delay={i * 0.1}>
                <div className="bg-white border border-border rounded-lg overflow-hidden hover:border-accent/30 transition-colors group">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={dest.image} 
                      alt={dest.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 right-3 bg-white rounded-md px-2 py-1 flex items-center gap-1">
                      <Star className="h-3.5 w-3.5 fill-accent text-accent" />
                      <span className="text-xs font-semibold">{dest.rating}</span>
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="text-xs text-accent font-medium mb-1">{dest.tagline}</p>
                    <h3 className="font-bold text-primary mb-1">{dest.name}</h3>
                    <p className="text-xs text-muted-foreground line-clamp-2">{dest.desc}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="relative order-2 lg:order-1">
                <img 
                  src="https://images.unsplash.com/photo-1519741497674-611481863552?w=800&q=80" 
                  alt="Romantic honeymoon" 
                  className="rounded-lg w-full h-96 object-cover"
                />
              </div>
              <div className="order-1 lg:order-2">
                <h2 className="heading-lg text-primary mb-6">What's Included</h2>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  Our honeymoon packages include everything you need for a perfect romantic getaway:
                </p>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {includedServices.map((service, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Heart className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                      <span className="text-sm text-foreground">{service}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="heading-lg text-primary-foreground mb-4">Start Your Forever, Beautifully</h2>
            <p className="text-primary-foreground/70 mb-8 max-w-xl mx-auto">
              Let us create the honeymoon you've always dreamed of. Get a personalized quote today.
            </p>
            <Link to="/contact">
              <Button variant="navySolid" size="xl" className="gap-2">
                Plan Your Honeymoon <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
}
